import { useCallback, useEffect, useRef, useState } from 'react';

export interface ChatMessage { role: 'user' | 'assistant'; content: string }

export function useKrysChat() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [pending, setPending] = useState('');
  const [streamingReply, setStreamingReply] = useState('');
  const [error, setError] = useState('');
  const [ready, setReady] = useState<boolean | null>(null);
  const [recording, setRecording] = useState(false);
  const sessionId = useRef<string>('');
  const conversation = useRef<ChatMessage[]>([]);
  const request = useRef<AbortController | null>(null);
  const healthRequest = useRef<AbortController | null>(null);

  const checkConnection = useCallback(async () => {
    healthRequest.current?.abort();
    const controller = new AbortController();
    healthRequest.current = controller;
    setReady(null);
    const timeout = window.setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch('/api/krys', { signal: controller.signal });
      const data = await response.json();
      if (healthRequest.current === controller) {
        setReady(response.ok && data.ready === true);
        setRecording(response.ok && data.recording === true);
      }
    } catch {
      if (healthRequest.current === controller) setReady(false);
    } finally { window.clearTimeout(timeout); }
  }, []);

  useEffect(() => {
    void checkConnection();
    return () => {
      healthRequest.current?.abort();
      healthRequest.current = null;
      request.current?.abort();
      request.current = null;
    };
  }, [checkConnection]);

  const cancel = useCallback(() => {
    request.current?.abort();
    request.current = null;
    setPending('');
    setStreamingReply('');
  }, []);

  const reset = useCallback(() => {
    cancel();
    conversation.current = [];
    setMessages([]);
    setError('');
  }, [cancel]);

  const send = useCallback(async (text: string): Promise<string | null> => {
    const content = text.trim();
    if (!content || content.length > 2000 || request.current || ready !== true) return null;
    if (!sessionId.current) sessionId.current = Array.from(crypto.getRandomValues(new Uint8Array(16)), n => n.toString(16).padStart(2, '0')).join('');
    const controller = new AbortController();
    request.current = controller;
    setPending(content);
    setStreamingReply('');
    setError('');
    const timeout = window.setTimeout(() => controller.abort(), 30_000);
    let revealTimer: ReturnType<typeof setInterval> | undefined;
    let displayed = 0;
    let receivedText = '';
    try {
      // Keep full turns; never start the API history with an assistant message.
      let history = conversation.current.slice(-12);
      while (history.reduce((sum, item) => sum + item.content.length, content.length) > 14000) history = history.slice(2);
      const response = await fetch('/api/krys', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, signal: controller.signal,
        body: JSON.stringify({ stream: true, sessionId: sessionId.current, messages: [...history, { role: 'user', content }] }),
      });
      let reply = '';
      if (!response.ok) {
        const data = await response.json();
        throw new Error(typeof data.error === 'string' ? data.error : 'The reply could not be delivered. Please try again.');
      }
      if (response.headers?.get('content-type')?.includes('application/x-ndjson') && response.body) {
        revealTimer = setInterval(() => {
          if (request.current !== controller || controller.signal.aborted) return;
          // Reveal at about 60 characters per second instead of provider bursts.
          const characters = Array.from(receivedText);
          displayed = Math.min(characters.length, displayed + 3);
          setStreamingReply(characters.slice(0, displayed).join(''));
        }, 50);
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '', complete = false;
        try {
          while (true) {
            const { value, done } = await reader.read();
            if (request.current !== controller) return null;
            buffer += decoder.decode(value, { stream: !done });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';
            for (const line of lines) {
              if (!line.trim()) continue;
              const chunk = JSON.parse(line);
              if (chunk.error) throw new Error(chunk.error);
              if (typeof chunk.delta === 'string') {
                reply += chunk.delta;
                receivedText = reply;
              }
              if (chunk.done === true) complete = true;
            }
            if (done) break;
          }
          if (!complete) throw new Error('Reply interrupted. Please try again.');
        } finally { await reader.cancel().catch(() => {}); }
      } else {
        const data = await response.json();
        reply = data.reply;
      }
      if (typeof reply !== 'string' || !reply.trim()) throw new Error('No reply arrived. Please try again.');
      window.clearTimeout(timeout);
      while (revealTimer && displayed < Array.from(reply).length) {
        if (request.current !== controller || controller.signal.aborted) return null;
        await new Promise(resolve => setTimeout(resolve, 50));
      }
      if (request.current !== controller || controller.signal.aborted) return null;
      conversation.current = [...conversation.current, { role: 'user', content }, { role: 'assistant', content: reply }].slice(-40) as ChatMessage[];
      setMessages(conversation.current);
      return reply;
    } catch (cause) {
      if (request.current === controller) {
        setError(controller.signal.aborted ? 'The reply took too long. Your question is still below — try again.'
          : cause instanceof Error ? cause.message : 'Connection lost. Please try again.');
      }
      return null;
    } finally {
      window.clearTimeout(timeout);
      if (revealTimer) clearInterval(revealTimer);
      if (request.current === controller) { request.current = null; setPending(''); setStreamingReply(''); }
    }
  }, [ready]);

  return { messages, pending, streamingReply, error, ready, recording, send, cancel, reset, checkConnection };
}
